import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import BundleWizardStepper, {
  WizardStep,
} from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import { toast } from "@/hooks/use-toast";
import { setupManagedEvent } from "@/features/supply-config/managed-events-api";
import {
  ManagedEventWizardData,
  managedEventWizardSchema,
} from "@/features/supply-config/ui/wizard/wizard-form";
import StepDetails from "@/features/supply-config/ui/wizard/step-details";
import StepIndividual from "@/features/supply-config/ui/wizard/step-individual";
import StepReview from "@/features/supply-config/ui/wizard/step-review";

const STEPS: WizardStep[] = [
  { key: "details", label: "Details" },
  { key: "individual", label: "Individual" },
  { key: "review", label: "Review" },
];

export default function ManagedEventCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const prefill = (location.state as any)?.prefill as
    | { name?: string; duration_minutes?: number | null }
    | undefined;

  const [current, setCurrent] = useState(0);

  const form = useForm<ManagedEventWizardData>({
    resolver: zodResolver(managedEventWizardSchema),
    defaultValues: {
      name: prefill?.name ?? "",
      team_ids: [],
      category: "SESSION",
      participant_scope: "INDIVIDUAL",
      duration_minutes: prefill?.duration_minutes ?? null,
      round_robin_pool_active: false,
      status: "ACTIVE",
    },
  });

  const stepKey = STEPS[current].key;
  const isLast = current === STEPS.length - 1;

  const setup = useMutation({
    mutationFn: () => {
      const v = form.getValues();
      return setupManagedEvent({
        team_ids: v.team_ids,
        name: v.name,
        category: v.category,
        participant_scope: v.participant_scope,
        duration_minutes: v.duration_minutes ?? null,
        round_robin_pool_active: v.round_robin_pool_active,
        status: v.status,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["managed-events"] });
      qc.invalidateQueries({ queryKey: ["event-types"] });
      toast({
        title: "Managed event created",
        description: `${res.created.individual} individual event type${
          res.created.individual === 1 ? "" : "s"
        } created`,
      });
      navigate(`../${res.managedEvent.id}`);
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const handleNext = async () => {
    if (stepKey === "details") {
      const ok = await form.trigger(["name", "team_ids", "category"]);
      if (!ok) return;
    }
    setCurrent((c) => Math.min(c + 1, STEPS.length - 1));
  };

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("..")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">New Managed Event</h1>
        </div>

        <BundleWizardStepper steps={STEPS} current={current} />

        <Form {...form}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="space-y-6 rounded-lg border border-gray-200 p-6"
          >
            {stepKey === "details" && <StepDetails form={form} />}
            {stepKey === "individual" && <StepIndividual form={form} />}
            {stepKey === "review" && <StepReview form={form} />}

            <div className="flex justify-between pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={
                  current === 0
                    ? () => navigate("..")
                    : () => setCurrent((c) => c - 1)
                }
              >
                {current === 0 ? "Cancel" : "← Back"}
              </Button>
              {isLast ? (
                <Button
                  type="button"
                  disabled={setup.isPending}
                  onClick={() => setup.mutate()}
                >
                  {setup.isPending ? "Creating…" : "✓ Create"}
                </Button>
              ) : (
                <Button type="button" onClick={handleNext}>
                  Next: {STEPS[current + 1].label} →
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </HasAccess>
  );
}
